export const OVERHEAD_COGS_BASES = ["materials_labor", "building_sq_ft", "per_unit"] as const;
export type OverheadCogsBasis = (typeof OVERHEAD_COGS_BASES)[number];

export const OVERHEAD_VARIANCE_DISPLAYS = ["percent", "dollars"] as const;
export type OverheadVarianceDisplay = (typeof OVERHEAD_VARIANCE_DISPLAYS)[number];

export const OVERHEAD_COLUMN_KEY = "overhead";

export const OVERHEAD_SQ_FT_UNAVAILABLE_NOTE =
  "Completed builds do not record square footage yet, so a square-foot rate stays blank.";

export const OVERHEAD_COGS_BASIS_OPTIONS: Array<{ id: OverheadCogsBasis; label: string }> = [
  { id: "materials_labor", label: "Percent of materials and labor" },
  { id: "building_sq_ft", label: "Dollars per square foot" },
  { id: "per_unit", label: "Dollars per completed build" },
];

export function parseOverheadCogsBasis(raw: unknown): OverheadCogsBasis | null {
  if (typeof raw !== "string") return null;
  return (OVERHEAD_COGS_BASES as readonly string[]).includes(raw) ? (raw as OverheadCogsBasis) : null;
}

export function parseOverheadVarianceDisplay(raw: unknown): OverheadVarianceDisplay | null {
  if (raw === "percent" || raw === "dollars") return raw;
  return null;
}

export function isOverheadCogsColumn(column: { metadata?: unknown }): boolean {
  const metadata = column.metadata;
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return false;
  return (metadata as Record<string, unknown>).overhead_linked === true;
}

/** Reject COGS edits that would change the overhead rule. Label and sort order stay allowed. */
export function overheadColumnLockError(
  column: { metadata?: unknown },
  body: Record<string, unknown>,
): string | null {
  if (!isOverheadCogsColumn(column)) return null;
  if ("band" in body && body.band !== "cogs") {
    return "Overhead stays in the COGS band. Move it within that band from COGS.";
  }
  if ("op" in body && body.op !== "add_usd") {
    return "Overhead math is set on the Overhead screen.";
  }
  if ("source_kind" in body && body.source_kind !== "none") {
    return "Overhead math is set on the Overhead screen.";
  }
  if ("source_module" in body && body.source_module !== "overhead") {
    return "Overhead stays linked to the Overhead screen.";
  }
  if ("source_band" in body && body.source_band != null) {
    return "Overhead math is set on the Overhead screen.";
  }
  if ("source_column_id" in body && body.source_column_id != null) {
    return "Overhead math is set on the Overhead screen.";
  }
  if ("is_active" in body) {
    return "Turn overhead on or off from the Overhead screen.";
  }
  if ("calc" in body || "applies_to" in body || "rate_kind" in body) {
    return "Overhead math is set on the Overhead screen.";
  }
  return null;
}

export function overheadPlantRates(input: {
  assumed_usd: number;
  actual_usd: number | null;
  activity: number;
  stated_goal_rate?: number | null;
}): { goal_rate: number | null; actual_rate: number | null; variance_rate: number | null } {
  const stated = input.stated_goal_rate;
  const hasStated = typeof stated === "number" && Number.isFinite(stated) && stated >= 0;
  if (!(input.activity > 0)) {
    return { goal_rate: hasStated ? stated : null, actual_rate: null, variance_rate: null };
  }
  const goal_rate = hasStated ? stated : input.assumed_usd / input.activity;
  if (input.actual_usd === null) {
    return { goal_rate, actual_rate: null, variance_rate: null };
  }
  const actual_rate = input.actual_usd / input.activity;
  return { goal_rate, actual_rate, variance_rate: actual_rate - goal_rate };
}

export function parseOverheadGoalRate(raw: unknown): number | null | "invalid" {
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw.trim()) : Number.NaN;
  if (!Number.isFinite(n) || n < 0) return "invalid";
  return n;
}

export function overheadGoalRateFromInput(basis: OverheadCogsBasis, raw: string): number | null | "invalid" {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return "invalid";
  return basis === "materials_labor" ? n / 100 : n;
}

export function overheadGoalInputValue(basis: OverheadCogsBasis | null, rate: number | null): string {
  if (basis == null || rate == null) return "";
  const shown = basis === "materials_labor" ? rate * 100 : rate;
  const digits = basis === "materials_labor" ? 4 : 2;
  return String(Number(shown.toFixed(digits)));
}

export function overheadGoalFieldLabel(basis: OverheadCogsBasis): string {
  if (basis === "per_unit") return "Goal dollars per build";
  if (basis === "building_sq_ft") return "Goal dollars per square foot";
  return "Goal percent";
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function signedFixed(value: number, digits: number): string {
  const rounded = Number(value.toFixed(digits));
  const text = rounded.toFixed(digits);
  return rounded > 0 ? `+${text}` : text;
}

function money(value: number): string {
  const rounded = round2(value);
  const body = Math.abs(rounded).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${rounded < 0 ? "-" : ""}$${body}`;
}

export function describeOverheadRate(input: {
  basis: OverheadCogsBasis;
  goal_rate: number | null;
  actual_rate: number | null;
  activity: number;
  goal_is_stated?: boolean;
}): string {
  if (input.basis === "building_sq_ft") {
    if (input.goal_is_stated && input.goal_rate != null) {
      return `Goal is ${money(input.goal_rate)} per square foot. ${OVERHEAD_SQ_FT_UNAVAILABLE_NOTE}`;
    }
    return OVERHEAD_SQ_FT_UNAVAILABLE_NOTE;
  }
  if (input.goal_is_stated && input.goal_rate != null) {
    const goal =
      input.basis === "materials_labor"
        ? `Goal is ${signedFixed(input.goal_rate * 100, 1).replace("+", "")}% of materials and labor.`
        : `Goal is ${money(input.goal_rate)} per build.`;
    if (!(input.activity > 0) || input.actual_rate === null) {
      return `${goal} Variance stays blank until a build is completed.`;
    }
    const actual =
      input.basis === "materials_labor"
        ? `Actual is ${signedFixed(input.actual_rate * 100, 1).replace("+", "")}% across ${money(input.activity)} of completed builds.`
        : `Actual is ${money(input.actual_rate)} per build across ${Math.round(input.activity)} completed builds.`;
    return `${goal} ${actual}`;
  }
  if (!(input.activity > 0) || input.goal_rate === null) {
    return input.basis === "per_unit"
      ? "No completed builds this period, so the rate is blank."
      : "No completed-build materials and labor this period, so the rate is blank.";
  }
  const actual =
    input.actual_rate === null
      ? ""
      : input.basis === "materials_labor"
        ? ` Actual is ${signedFixed(input.actual_rate * 100, 1).replace("+", "")}%.`
        : ` Actual is ${money(input.actual_rate)} per build.`;
  if (input.basis === "materials_labor") {
    return `Goal is ${signedFixed(input.goal_rate * 100, 1).replace("+", "")}% of materials and labor across ${money(input.activity)} of completed builds.${actual}`;
  }
  const count = Math.round(input.activity);
  return `Goal is ${money(input.goal_rate)} per build across ${count} completed build${count === 1 ? "" : "s"}.${actual}`;
}

export function overheadVarianceLabel(input: {
  basis: OverheadCogsBasis;
  display: OverheadVarianceDisplay;
  goal_rate: number | null;
  variance_rate: number | null;
  variance_usd: number | null;
}): string | null {
  if (input.variance_rate === null || input.variance_usd === null) return null;
  if (round2(input.variance_usd) === 0) return null;
  if (input.display === "dollars") return `var ${signedMoney(input.variance_usd)}`;
  if (input.basis === "materials_labor") {
    return `var ${signedFixed(input.variance_rate * 100, 1)}%`;
  }
  if (input.goal_rate) {
    return `var ${signedFixed((input.variance_rate / input.goal_rate) * 100, 1)}%`;
  }
  return `var ${signedMoney(input.variance_rate)}`;
}

function signedMoney(value: number): string {
  const rounded = round2(value);
  const body = Math.abs(rounded).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (rounded > 0) return `+$${body}`;
  if (rounded < 0) return `-$${body}`;
  return `$${body}`;
}

export function overheadProductShare(input: {
  basis: OverheadCogsBasis;
  display: OverheadVarianceDisplay;
  recipe_kind: string | null | undefined;
  goal_rate: number | null;
  variance_rate: number | null;
  materials_usd: number;
  labor_usd: number;
  sq_ft: number | null;
}): { goal_usd: number | null; variance_label: string | null; missing_size: boolean } {
  const kind = input.recipe_kind ?? "product";
  if (kind !== "product") {
    return { goal_usd: null, variance_label: null, missing_size: false };
  }
  if (input.basis === "building_sq_ft" && !(input.sq_ft != null && input.sq_ft > 0)) {
    return { goal_usd: null, variance_label: null, missing_size: input.goal_rate != null };
  }
  if (input.goal_rate === null) {
    return { goal_usd: null, variance_label: null, missing_size: false };
  }
  const base =
    input.basis === "materials_labor"
      ? input.materials_usd + input.labor_usd
      : input.basis === "per_unit"
        ? 1
        : (input.sq_ft as number);
  const goal_usd = round2(input.goal_rate * base);
  const variance_usd = input.variance_rate === null ? null : round2(input.variance_rate * base);
  return {
    goal_usd,
    variance_label: overheadVarianceLabel({
      basis: input.basis,
      display: input.display,
      goal_rate: input.goal_rate,
      variance_rate: input.variance_rate,
      variance_usd,
    }),
    missing_size: false,
  };
}
